import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  ButtonSelect,
  Icon,
  InputField,
  MenuMulti,
  type MenuMultiOption,
  Switch,
} from '@saviynt/design-system';

import type { RootState } from '../../../../redux/obNextGenStore';
import {
  type IntegrationFilterState,
  setSearchQuery,
  setSelectedTypes,
  setShowHiddenApps,
} from '../../data-store/integrationSearchFilterSlice';

import './SearchFilterViewBar.css';

/**
 * TODO (Chicago release)
 * - The commented features will be enabled
 */
function SearchFilterViewBar(): JSX.Element {
  const [isFilterOpen, setFilterOpen] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { searchQuery, showHiddenApps, selectedTypes } = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    (state: RootState): IntegrationFilterState =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      state.discoveryAllTabSearchToggelFilter
  );

  const [localSearch, setLocalSearch] = useState<string>(searchQuery);

  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(setSearchQuery(localSearch));
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [localSearch, dispatch]);

  const handleToggleChange = () => {
    dispatch(setShowHiddenApps(!showHiddenApps));
  };

  const filterConfigs = useMemo(
    () => [
      {
        key: 'Type',
        label: 'Type',
        options: [
          {
            label: 'Discovered Applications',
            value: 'DISCOVERED_APP',
            isChecked: selectedTypes.includes('DISCOVERED_APP'),
          },
          {
            label: 'Discovery Sources',
            value: 'DISCOVERY_SOURCE',
            isChecked: selectedTypes.includes('DISCOVERY_SOURCE'),
          },
          {
            label: 'Onboarded Applications',
            value: 'ONBOARDED_APP',
            isChecked: selectedTypes.includes('ONBOARDED_APP'),
          },
        ],
      },
    ],
    [selectedTypes]
  );

  const handleChangeFilter = () => (updatedOptions?: MenuMultiOption[]) => {
    if (!updatedOptions) {
      return;
    }

    const selectedLabels: string[] = updatedOptions
      .filter((opt) => opt.isChecked)
      .map((opt) => opt.value);

    dispatch(setSelectedTypes(selectedLabels.join(',')));
  };

  return (
    <>
      <Box
        className='IntegrationsPageToolbox-searchFilterGroup'
        dataTestId='toolbox-search-filter-group'>
        <Box
          className='IntegrationsPageToolbox-searchbox'
          dataTestId='integrations-dashboard-search-box'>
          <InputField
            name='searchIntegration'
            placeholder='Search integrations'
            kind='search'
            prefixIcon={<Icon kind='search' color='Neutral-600' />}
            value={localSearch}
            setValue={setLocalSearch}
            dataTestId='integrations-dashboard-search-input'
            size='medium'
          />
          <Box
            className='IntegrationsPageToolbox-filterbox'
            dataTestId='integrations-dashboard-filter-box'>
            <ButtonSelect
              label='Filter'
              onClick={() => setFilterOpen((prev) => !prev)}
              isOpen={isFilterOpen}
              size='medium'
              prefixIcon={<Icon kind='Filter' />}
              dataTestId='integrations-dashboard-filter-button'
              isVisualCueEnabled={false}
            />
          </Box>
        </Box>
        <Box>
          <Switch
            isChecked={showHiddenApps}
            onChange={() => handleToggleChange()}
            label='Show Hidden Applications'
            labelPosition='left'
            dataTestId='integrations-dashboard-hiddenapps-toggle'
            className='IntegrationsPageToolbox-ShowHiddenApps'
          />
        </Box>
      </Box>
      <Box className='IntegrationsPageToolbox-filterRow'>
        {isFilterOpen && (
          <Box>
            {filterConfigs.map(({ label, options, key }) => (
              <MenuMulti
                key={key}
                options={options}
                label={label}
                onChange={handleChangeFilter()}
                dataTestId={`integrations-filter-${key}`}
                trigger={
                  <ButtonSelect
                    kind='pill'
                    label={label}
                    dataTestId={`integrations-filter-trigger-${key}`}
                  />
                }
                appendLabelsToButton={false}
                heightToShowScrollbar='8rem'
                minOptionsForSearch={4}
                sortOnCheck={false}
              />
            ))}
          </Box>
        )}
      </Box>
    </>
  );
}

export default SearchFilterViewBar;
